// LuviaRx — Admin "Sales" tab: automatic, scheduled storewide sales.
import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Plus, Pencil, Trash2, Loader2, Leaf } from "lucide-react";
import { Switch as BaseSwitch } from "../../components/ui/switch";
import { useCart } from "../../context/CartContext";

const Switch = (props) => <BaseSwitch {...props} className="data-[state=checked]:bg-[#8B2252]" />;

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMPTY = { name: "", description: "", percent: 20, product_ids: [], start_date: "", end_date: "", enabled: false, allow_first10: false, accent_color: "", icon: "leaf" };

const errText = (e) => {
  const d = e?.response?.data?.detail;
  if (Array.isArray(d)) return d.map((x) => x.msg?.replace(/^Value error, /, "")).join(" · ");
  return d || "Something went wrong";
};

const statusOf = (s) => {
  const today = new Date().toISOString().slice(0, 10);
  if (!s.enabled) return ["Off", "bg-charcoal-100 text-charcoal-500"];
  if (s.is_live) return ["Live now", "bg-[#8B2252] text-white"];
  if (s.start_date > today) return ["Scheduled", "bg-amber-100 text-amber-800"];
  return ["Ended", "bg-charcoal-100 text-charcoal-400"];
};

export default function SalesTab({ token }) {
  const { refreshSeasonal } = useCart();
  const auth = { headers: { Authorization: `Bearer ${token}` } };
  const [sales, setSales] = useState([]);
  const [products, setProducts] = useState([]);
  const [editing, setEditing] = useState(null);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    const [s, p] = await Promise.all([
      axios.get(`${API}/admin/sales`, { headers: { Authorization: `Bearer ${token}` } }),
      axios.get(`${API}/products`),
    ]);
    setSales(s.data);
    setProducts(p.data);
    refreshSeasonal();
  }, [token, refreshSeasonal]);

  useEffect(() => { load().catch((e) => setError(errText(e))); }, [load]);

  const save = async (form) => {
    const payload = { ...form, percent: Number(form.percent) };
    if (form.id) await axios.put(`${API}/admin/sales/${form.id}`, payload, auth);
    else await axios.post(`${API}/admin/sales`, payload, auth);
    setEditing(null);
    await load();
  };

  const toggle = async (s, field) => {
    setError("");
    try {
      const { id, is_live, created_at, updated_at, ...rest } = s;
      await axios.put(`${API}/admin/sales/${id}`, { ...rest, [field]: !s[field] }, auth);
      await load();
    } catch (e) { setError(errText(e)); }
  };

  const remove = async (s) => {
    if (!window.confirm(`Delete the sale "${s.name}"?`)) return;
    setError("");
    try {
      await axios.delete(`${API}/admin/sales/${s.id}`, auth);
      await load();
    } catch (e) { setError(errText(e)); }
  };

  const nameOf = (slug) => products.find((p) => p.slug === slug)?.name || slug;

  return (
    <div data-testid="admin-sales-panel" className="space-y-6">
      <div className="surface-card p-6 flex flex-wrap items-center justify-between gap-4">
        <div className="max-w-2xl">
          <p className="overline mb-1">Automatic sales</p>
          <p className="text-sm text-charcoal-500">
            Sales take a % off chosen products automatically, with no code needed, between the start and end dates.
            Only one sale can be on at a time. A live sale shows up on the homepage, the products banner, product
            pages (with a countdown) and the top bar. It can't be combined with other discounts, unless you allow FIRST10.
          </p>
        </div>
        <button
          type="button"
          onClick={() => setEditing({ ...EMPTY })}
          data-testid="admin-sales-new-btn"
          className="btn-primary inline-flex items-center gap-1.5"
        >
          <Plus className="w-4 h-4" /> New sale
        </button>
      </div>

      {error && <p data-testid="admin-sales-error" className="text-sm text-red-600">{error}</p>}

      {editing && (
        <SaleForm initial={editing} products={products} onCancel={() => setEditing(null)} onSave={save} />
      )}

      <div className="space-y-3">
        {sales.length === 0 && <p className="text-sm text-charcoal-400 italic">No sales yet.</p>}
        {sales.map((s) => {
          const [label, cls] = statusOf(s);
          return (
            <div key={s.id} data-testid={`admin-sale-row-${s.id}`} className="surface-card p-5 flex flex-wrap items-center gap-5">
              <div className="flex-1 min-w-[240px]">
                <div className="flex items-center gap-2 flex-wrap">
                  <Leaf className="w-4 h-4 text-[#8B2252]" />
                  <p className="font-display font-bold text-lg text-charcoal-600">{s.name}</p>
                  <span data-testid={`admin-sale-status-${s.id}`} className={`text-[10px] font-semibold uppercase tracking-wider px-2.5 py-0.5 rounded-full ${cls}`}>{label}</span>
                </div>
                <p className="text-sm text-charcoal-500 mt-1">
                  <span className="font-semibold text-[#8B2252]">{s.percent}% off</span> · {s.product_ids.map(nameOf).join(", ")}
                </p>
                <p className="text-xs text-charcoal-400 mt-0.5">{s.start_date} → {s.end_date}</p>
              </div>
              <label className="flex items-center gap-2 text-xs text-charcoal-500">
                <Switch checked={s.enabled} onCheckedChange={() => toggle(s, "enabled")} data-testid={`admin-sale-enabled-${s.id}`} />
                On
              </label>
              <label className="flex items-center gap-2 text-xs text-charcoal-500">
                <Switch checked={s.allow_first10} onCheckedChange={() => toggle(s, "allow_first10")} data-testid={`admin-sale-first10-${s.id}`} />
                Allow FIRST10
              </label>
              <div className="flex items-center gap-1">
                <button type="button" onClick={() => setEditing(s)} data-testid={`admin-sale-edit-${s.id}`} className="p-2 rounded-full hover:bg-cream-200" aria-label="Edit sale"><Pencil className="w-4 h-4" /></button>
                <button type="button" onClick={() => remove(s)} data-testid={`admin-sale-delete-${s.id}`} className="p-2 rounded-full hover:bg-red-50 text-red-600" aria-label="Delete sale"><Trash2 className="w-4 h-4" /></button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

const SaleForm = ({ initial, products, onCancel, onSave }) => {
  const [form, setForm] = useState(initial);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleProduct = (slug) => set("product_ids",
    form.product_ids.includes(slug) ? form.product_ids.filter((x) => x !== slug) : [...form.product_ids, slug]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setError("");
    try { await onSave(form); } catch (err) { setError(errText(err)); } finally { setBusy(false); }
  };

  return (
    <form onSubmit={submit} data-testid="admin-sale-form" className="surface-card p-6 space-y-5">
      <p className="overline">{form.id ? "Edit sale" : "New sale"}</p>
      <div className="grid md:grid-cols-[2fr_1fr] gap-4">
        <label className="block text-xs uppercase tracking-[0.18em] text-charcoal-400">Sale name
          <input data-testid="admin-sale-name-input" className="input mt-1.5" value={form.name} maxLength={60}
            onChange={(e) => set("name", e.target.value)} placeholder="e.g. Trick or Treat Yourself" required />
        </label>
        <label className="block text-xs uppercase tracking-[0.18em] text-charcoal-400">% off
          <input data-testid="admin-sale-percent-input" type="number" min={1} max={90} className="input mt-1.5"
            value={form.percent} onChange={(e) => set("percent", e.target.value)} required />
        </label>
      </div>
      <label className="block text-xs uppercase tracking-[0.18em] text-charcoal-400">Short description (shown to shoppers)
        <input data-testid="admin-sale-description-input" className="input mt-1.5" value={form.description} maxLength={300}
          onChange={(e) => set("description", e.target.value)} placeholder="e.g. 25% off every size, applied automatically at checkout." />
      </label>
      <div className="grid sm:grid-cols-2 gap-4">
        <label className="block text-xs uppercase tracking-[0.18em] text-charcoal-400">Starts
          <input data-testid="admin-sale-start-input" type="date" className="input mt-1.5" value={form.start_date}
            onChange={(e) => set("start_date", e.target.value)} required />
        </label>
        <label className="block text-xs uppercase tracking-[0.18em] text-charcoal-400">Ends (last day of the sale)
          <input data-testid="admin-sale-end-input" type="date" className="input mt-1.5" value={form.end_date}
            onChange={(e) => set("end_date", e.target.value)} required />
        </label>
      </div>
      <div>
        <p className="text-xs uppercase tracking-[0.18em] text-charcoal-400 mb-2">Products on sale (every size)</p>
        <div className="flex flex-wrap gap-2" data-testid="admin-sale-products">
          {products.map((p) => {
            const on = form.product_ids.includes(p.slug);
            return (
              <button key={p.slug} type="button" onClick={() => toggleProduct(p.slug)}
                data-testid={`admin-sale-product-${p.slug}`} aria-pressed={on}
                className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${on ? "bg-[#8B2252] text-white border-[#8B2252]" : "border-charcoal-200 text-charcoal-600 hover:border-charcoal-400"}`}>
                {p.name}
              </button>
            );
          })}
        </div>
      </div>
      <div className="grid sm:grid-cols-2 gap-4">
        <div className="block text-xs uppercase tracking-[0.18em] text-charcoal-400">Accent colour (button + % off)
          <div className="mt-1.5 flex items-center gap-2">
            <input type="color" value={form.accent_color || "#8B2252"} onChange={(e) => set("accent_color", e.target.value.toUpperCase())}
              data-testid="admin-sale-accent-picker" className="h-10 w-12 rounded-lg border border-charcoal-200 bg-white p-1" aria-label="Accent colour" />
            <input data-testid="admin-sale-accent-input" className="input" value={form.accent_color} maxLength={7}
              onChange={(e) => set("accent_color", e.target.value.trim())} placeholder="#C9703C (blank = burgundy)" />
          </div>
        </div>
        <label className="block text-xs uppercase tracking-[0.18em] text-charcoal-400">Seasonal icon
          <select data-testid="admin-sale-icon-select" className="input mt-1.5" value={form.icon || "leaf"} onChange={(e) => set("icon", e.target.value)}>
            <option value="leaf">Leaf</option>
            <option value="ghost">Ghost</option>
          </select>
        </label>
      </div>
      <div className="flex flex-wrap gap-6">
        <label className="flex items-center gap-2 text-sm text-charcoal-600">
          <Switch checked={form.enabled} onCheckedChange={(v) => set("enabled", v)} data-testid="admin-sale-form-enabled" />
          Turn on (switches off any other sale)
        </label>
        <label className="flex items-center gap-2 text-sm text-charcoal-600">
          <Switch checked={form.allow_first10} onCheckedChange={(v) => set("allow_first10", v)} data-testid="admin-sale-form-first10" />
          Let first-time customers stack FIRST10
        </label>
      </div>
      {error && <p data-testid="admin-sale-form-error" className="text-sm text-red-600">{error}</p>}
      <div className="flex gap-3">
        <button type="submit" disabled={busy} data-testid="admin-sale-save-btn" className="btn-primary inline-flex items-center gap-1.5">
          {busy && <Loader2 className="w-4 h-4 animate-spin" />} Save sale
        </button>
        <button type="button" onClick={onCancel} data-testid="admin-sale-cancel-btn" className="btn-ghost">Cancel</button>
      </div>
    </form>
  );
};
